import type { VisualShape, VisualState } from 'types'
import {
  AGENT_SHAPES,
  CHAT_PRESENCE_SHAPES,
  CHAT_RESEARCH_ANIM_SHAPES,
  CHAT_TOOL_SHAPES,
} from './canvas-shape-taxonomy.js'

export type CanvasSurface = 'chat' | 'landing'

export interface ChatIntentPayload {
  shape: VisualShape | null
  weatherData?: VisualState['weatherData']
  mapData?: VisualState['mapData']
  chartData?: VisualState['chartData']
  textContent?: string
}

export function hasRenderableWeatherData(data?: VisualState['weatherData']): boolean {
  if (!data) return false
  if (data.temperatureValue != null && Number.isFinite(data.temperatureValue)) return true
  if (data.temperature && /\d/.test(data.temperature)) return true
  return false
}

export function hasRenderableMapData(data?: VisualState['mapData']): boolean {
  if (!data) return false
  return (
    data.lat != null &&
    data.lng != null &&
    Number.isFinite(data.lat) &&
    Number.isFinite(data.lng)
  )
}

export function hasRenderableChartData(data?: VisualState['chartData']): boolean {
  if (!data) return false
  if (data.numericValue != null && Number.isFinite(data.numericValue)) return true
  if (data.value && /\d/.test(data.value)) return true
  return data.trend !== 'neutral' && Boolean(data.label)
}

export function isShapeAllowedOnChat(
  shape: VisualShape,
  payload?: Partial<VisualState>,
): boolean {
  if (CHAT_PRESENCE_SHAPES.has(shape)) return true
  if (AGENT_SHAPES.has(shape)) return true
  if (CHAT_RESEARCH_ANIM_SHAPES.has(shape)) return true
  if (CHAT_TOOL_SHAPES.has(shape)) return true
  if (shape === 'text') return Boolean(payload?.textContent)
  if (shape === 'weather') return hasRenderableWeatherData(payload?.weatherData)
  if (shape === 'map') return hasRenderableMapData(payload?.mapData)
  if (shape === 'chart') return hasRenderableChartData(payload?.chartData)
  return false
}

function stripLandingPayload(state: VisualState): VisualState {
  const keepWeather = state.shape === 'weather'
  const keepMap = state.shape === 'map'
  const keepChart = state.shape === 'chart'
  const keepText = state.shape === 'text'
  const keepMolecule = state.shape === 'molecule'

  const next: VisualState = { ...state }
  if (!keepWeather) delete next.weatherData
  if (!keepMap) delete next.mapData
  if (!keepChart) delete next.chartData
  if (!keepText) delete next.textContent
  delete next.solarSystemData
  delete next.stockData
  delete next.networkData
  if (!keepMolecule) delete next.moleculeData
  delete next.mathData
  return next
}

/** Drop landing-only shapes/payload before merging into future-chat canvas. */
export function sanitizeVisualStateForChat(state: VisualState): VisualState {
  if (isShapeAllowedOnChat(state.shape, state)) {
    return stripLandingPayload(state)
  }

  return stripLandingPayload({
    ...state,
    shape: 'orb',
    mode: state.mode === 'idle' ? 'thinking' : state.mode,
  })
}

/** Returns null when intent should not override chat context (e.g. weather without API data). */
export function sanitizeIntentForChat(result: ChatIntentPayload): ChatIntentPayload | null {
  if (!result.shape) return null

  const payload: Partial<VisualState> = {}
  if (result.weatherData) payload.weatherData = result.weatherData
  if (result.mapData) payload.mapData = result.mapData
  if (result.chartData) payload.chartData = result.chartData
  if (result.textContent) payload.textContent = result.textContent

  if (!isShapeAllowedOnChat(result.shape, payload)) {
    return null
  }

  return result
}
